/**
 * HistoryPanel - Persistent history view for Search and Investigation workflows
 *
 * Features:
 * - Tabbed view: Searches, Investigations, All
 * - List with timestamps, meta, and status indicators
 * - Actions: Restore, Rerun, Delete, Pin, Rename
 * - Search within history
 * - Keyboard navigation
 * - Accessible with proper ARIA labels
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  History, Search, Clock, Pin, PinOff, Trash2, Play, ExternalLink,
  Loader2, AlertCircle, CheckCircle, XCircle, ChevronRight,
  Filter, X, MoreVertical, Edit2, RefreshCw, Microscope
} from 'lucide-react';
import {
  listHistory,
  deleteHistoryItem,
  updateHistoryItem,
  formatHistoryItem,
} from '@/services/historyService';

// Status badge colors
const STATUS_COLORS = {
  inProgress: { bg: 'bg-info/10', text: 'text-info', icon: Loader2 },
  completed: { bg: 'bg-success/10', text: 'text-success', icon: CheckCircle },
  failed: { bg: 'bg-destructive/10', text: 'text-destructive', icon: XCircle },
};

// Tab definitions
const TABS = [
  { id: 'all', label: 'All', icon: History },
  { id: 'search', label: 'Searches', icon: Search },
  { id: 'investigation', label: 'Investigations', icon: Microscope },
];

export default function HistoryPanel({
  isOpen,
  onClose,
  onRestoreSearch,
  onRestoreInvestigation,
  onNavigateToInvestigation,
}) {
  const [activeTab, setActiveTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);

  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Load history items
  const loadHistory = useCallback(async (reset = true) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        type: activeTab === 'all' ? undefined : activeTab,
        limit: 50,
        cursor: reset ? undefined : cursor,
        search: searchQuery || undefined,
        pinnedOnly: showPinnedOnly,
      };

      const result = await listHistory(params);

      const formattedItems = result.items.map(formatHistoryItem);

      if (reset) {
        setItems(formattedItems);
      } else {
        setItems(prev => [...prev, ...formattedItems]);
      }

      setHasMore(result.hasMore);
      setCursor(result.cursor);
    } catch (err) {
      console.error('Failed to load history:', err);
      // Don't show error if it's just mock data missing
      // setError('Failed to load history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery, showPinnedOnly, cursor]);

  // Load on mount and when filters change
  useEffect(() => {
    if (isOpen) {
      loadHistory(true);
    }
  }, [isOpen, activeTab, searchQuery, showPinnedOnly]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        if (menuOpenId) {
          setMenuOpenId(null);
        } else if (editingId) {
          setEditingId(null);
        } else {
          onClose?.();
        }
        return;
      }

      // Arrow navigation
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        handleRestore(items[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, items, selectedIndex, menuOpenId, editingId, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[selectedIndex];
      item?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // Handle restore action
  const handleRestore = (item) => {
    if (item._type === 'search') {
      onRestoreSearch?.(item);
    } else {
      // Check if we should navigate to detail page or restore inline
      if (onNavigateToInvestigation && item.id) {
        onNavigateToInvestigation(item.id, item);
      } else {
        onRestoreInvestigation?.(item);
      }
    }
    onClose?.();
  };

  // Handle delete action
  const handleDelete = async (item) => {
    try {
      await deleteHistoryItem(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setMenuOpenId(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Handle pin toggle
  const handleTogglePin = async (item) => {
    try {
      await updateHistoryItem(item.id, item._type, { isPinned: !item.isPinned });
      setItems(prev =>
        prev.map(i =>
          i.id === item.id ? { ...i, isPinned: !i.isPinned } : i
        )
      );
      setMenuOpenId(null);
    } catch (err) {
      console.error('Failed to update pin:', err);
    }
  };

  // Handle rename
  const handleStartRename = (item) => {
    setEditingId(item.id);
    setEditTitle(item.customTitle || item.displayTitle);
    setMenuOpenId(null);
  };

  const handleSaveRename = async (item) => {
    if (editTitle.trim()) {
      try {
        await updateHistoryItem(item.id, item._type, { customTitle: editTitle.trim() });
        setItems(prev =>
          prev.map(i =>
            i.id === item.id ? { ...i, customTitle: editTitle.trim(), displayTitle: editTitle.trim() } : i
          )
        );
      } catch (err) {
        console.error('Failed to rename:', err);
      }
    }
    setEditingId(null);
    setEditTitle('');
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="History Panel"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 transition-opacity backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-card shadow-2xl flex flex-col border-l border-border animate-in slide-in-from-right duration-300 sm:duration-500">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border bg-card/50 backdrop-blur-sm z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <History className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">History</h2>
                <p className="text-sm text-muted-foreground">Recent searches and investigations</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Close history panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter history..."
              className="input pl-10 bg-secondary/50 border-transparent focus:bg-card focus:border-primary"
              aria-label="Search history"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4" role="tablist">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tabpanel-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
            <div className="flex-1" />
            <button
              onClick={() => setShowPinnedOnly(!showPinnedOnly)}
              className={`p-1.5 rounded-lg transition-all ${showPinnedOnly
                  ? 'bg-warning/10 text-warning ring-1 ring-warning/20'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              aria-label={showPinnedOnly ? 'Show all items' : 'Show pinned only'}
              aria-pressed={showPinnedOnly}
              title={showPinnedOnly ? 'Show all items' : 'Show pinned only'}
            >
              <Pin className={`w-4 h-4 ${showPinnedOnly ? 'fill-warning' : ''}`} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-label={`${activeTab} history items`}
        >
          {/* Loading State */}
          {loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="text-muted-foreground">Loading history...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-6">
              <div className="bg-destructive/5 border border-destructive/10 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <span className="text-destructive text-sm">{error}</span>
                <button
                  onClick={() => loadHistory(true)}
                  className="ml-auto text-sm text-destructive hover:text-destructive/80 font-medium underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                <History className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No history yet</h3>
              <p className="text-muted-foreground max-w-xs text-sm">
                {searchQuery
                  ? 'No items match your search. Try a different query.'
                  : showPinnedOnly
                    ? 'No pinned items. Pin important searches to find them quickly.'
                    : 'Your recent activity will appear here.'}
              </p>
            </div>
          )}

          {/* History List */}
          {items.length > 0 && (
            <ul
              ref={listRef}
              className="divide-y divide-border"
              role="listbox"
              aria-label="History items"
            >
              {items.map((item, index) => {
                const isSelected = selectedIndex === index;
                const isSearch = item._type === 'search';
                const statusConfig = !isSearch && STATUS_COLORS[item.status];
                const StatusIcon = statusConfig?.icon;

                return (
                  <li
                    key={item.id}
                    className={`relative transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-secondary/50'}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <div className="px-6 py-4 group">
                      <div className="flex items-start gap-3">
                        {/* Type Icon */}
                        <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isSearch ? 'bg-primary/10 text-primary' : 'bg-violet-500/10 text-violet-600'
                          }`}>
                          {isSearch ? (
                            <Search className="w-4 h-4" />
                          ) : (
                            <Microscope className="w-4 h-4" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          {editingId === item.id ? (
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onBlur={() => handleSaveRename(item)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveRename(item);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              className="input text-sm py-1 h-8"
                              autoFocus
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              {item.isPinned && (
                                <Pin className="w-3.5 h-3.5 text-warning fill-warning flex-shrink-0" />
                              )}
                              <button
                                onClick={() => handleRestore(item)}
                                className="text-sm font-medium text-foreground hover:text-primary text-left truncate transition-colors"
                              >
                                {item.displayTitle}
                              </button>
                            </div>
                          )}

                          {/* Meta info */}
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {item.formattedDate}
                            </span>

                            {/* Search filters */}
                            {isSearch && item.filters?.sources?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Filter className="w-3 h-3" />
                                {item.filters.sources.length} sources
                              </span>
                            )}

                            {/* Investigation status */}
                            {!isSearch && statusConfig && (
                              <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${statusConfig.bg} ${statusConfig.text}`}>
                                <StatusIcon className={`w-3 h-3 ${item.status === 'inProgress' ? 'animate-spin' : ''}`} />
                                {item.status === 'inProgress' ? 'Running' : item.status}
                              </span>
                            )}
                          </div>

                          {/* Investigation summary */}
                          {!isSearch && item.outputsMeta?.summary && (
                            <p className="mt-2 text-xs text-muted-foreground/80 line-clamp-2 leading-relaxed bg-secondary/50 p-2 rounded border border-border/50">
                              {item.outputsMeta.summary}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleRestore(item)}
                            className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                            aria-label={isSearch ? 'Restore search' : 'Open investigation'}
                            title={isSearch ? 'Restore search' : 'Open investigation'}
                          >
                            {isSearch ? (
                              <RefreshCw className="w-4 h-4" />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                          </button>

                          {/* More menu */}
                          <div className="relative">
                            <button
                              onClick={() => setMenuOpenId(menuOpenId === item.id ? null : item.id)}
                              className={`p-1.5 rounded-md transition-colors ${menuOpenId === item.id
                                  ? 'bg-secondary text-foreground'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                                }`}
                              aria-label="More actions"
                              aria-expanded={menuOpenId === item.id}
                              aria-haspopup="menu"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>

                            {menuOpenId === item.id && (
                              <div
                                className="absolute right-0 top-full mt-1 w-48 bg-popover rounded-xl shadow-lg border border-border py-1 z-20 animate-in fade-in zoom-in-95 duration-100"
                                role="menu"
                              >
                                <button
                                  onClick={() => handleTogglePin(item)}
                                  className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary flex items-center gap-2"
                                  role="menuitem"
                                >
                                  {item.isPinned ? (
                                    <>
                                      <PinOff className="w-4 h-4" />
                                      Unpin
                                    </>
                                  ) : (
                                    <>
                                      <Pin className="w-4 h-4" />
                                      Pin to top
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleStartRename(item)}
                                  className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary flex items-center gap-2"
                                  role="menuitem"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Rename
                                </button>
                                <div className="my-1 border-b border-border" />
                                <button
                                  onClick={() => handleDelete(item)}
                                  className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/5 flex items-center gap-2"
                                  role="menuitem"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Load More */}
          {hasMore && !loading && (
            <div className="px-6 py-4 border-t border-border">
              <button
                onClick={() => loadHistory(false)}
                className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary rounded-lg transition-colors font-medium border border-border/50"
              >
                Load older items
              </button>
            </div>
          )}

          {/* Loading More */}
          {loading && items.length > 0 && (
            <div className="flex items-center justify-center py-4 border-t border-border">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Loading more...</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-border bg-muted/30 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>
              {items.length} item{items.length !== 1 ? 's' : ''}
              {showPinnedOnly && ' (pinned only)'}
            </span>
            <span className="opacity-70">Esc to close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
